import React from 'react'

import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle as faCheckCircleLight, faEyeSlash, faLock } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Checkbox, ListItem, Text,
} from '@domain-group/suit-theme'

import useCampaignQuoteTemplateItem from '@hooks/useCampaignQuoteTemplateItem'
import useProvider from '@hooks/useProvider'

import type { CampaignQuoteTemplateItemModel } from '@models/types'

import HintWrapper from '@components/HintWrapper'

import { quoteItemSettingsHiddenText } from '@functions/campaignQuoteItem'

interface CampaignQuoteTemplateItemProps {
  campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel,
  dragHandleProps: object,
  dragSnapshot: {
    isDragging: boolean,
  },
  draggableProps: object,
  hideDates: boolean,
  hidePrices: boolean,
}

const CampaignQuoteTemplateItem = React.forwardRef((props: CampaignQuoteTemplateItemProps, ref) => {
  const {
    // callbacks: { showCreateOrEditQuoteItemModal },
    campaignQuoteTemplateItem,
    dragHandleProps,
    dragSnapshot,
    draggableProps,
    hideDates,
    hidePrices,
  } = props

  const hidden = digObject(campaignQuoteTemplateItem, 'data.hidden', false)

  const { isDragging } = dragSnapshot || {}

  const campaignQuoteTemplateItemPayload = useCampaignQuoteTemplateItem(campaignQuoteTemplateItem)
  const {
    callbacks: {
      createOrEditCampaignQuoteTemplateItem,
      launchPriceAutoUpdatedModal,
      toggleCampaignQuoteTemplateItemSelection,
    },
    campaignQuoteTemplateItem: {
      display_title,
      id,
      locked,
      price_override,
      provider_id,
      selected,
    },
    // @ts-expect-error TS2339
    requiredByDate,
  } = campaignQuoteTemplateItemPayload

  const priceListUpdated = campaignQuoteTemplateItem.data?.auto_price_update?.updated === true
    && !campaignQuoteTemplateItem.data?.auto_price_update?.seen

  const { provider: { title: providerTitle } } = useProvider({ id: provider_id })

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        backgroundColor: 'white',
        borderBottom: '1px dashed',
        borderColor: 'lineColor',
        paddingY: 'medium',
      }}
      forwardProps={{
        ...draggableProps,
        ...dragHandleProps,
      }}
      isDragging={isDragging}
      key={id}
      onClick={() => createOrEditCampaignQuoteTemplateItem()}
      // @ts-expect-error TS2322
      ref={ref}
      showAngleIcon
    >
      <Checkbox
        checked={selected}
        iconChecked={locked ? faLock : faCheckCircle}
        iconDefault={locked ? faLock : faCheckCircleLight}
        color={selected && !locked ? 'green' : 'lightGrey'}
        // @ts-expect-error TS7006
        onClick={(e) => {
          if (!locked){
            toggleCampaignQuoteTemplateItemSelection(e, {
              include_campaign_quote_template_item_quote_dynamic_items: true,
            })
          }
        }}
      />

      <Box flexDirection="column" lineHeight="1.3">
        <Box alignItems="center" style={{ gap: '6px' }}>
          <HintWrapper
            hint={providerTitle || 'Unknown Provider'}
            renderChildrenOnly={!providerTitle}
          >
            <Text
              color={hidden ? 'faintGrey' : 'bodyFontColor'}
              fontSize="small"
              lineHeight="1.3"
              variant="twoLineEllipsis"
            >
              {display_title}
            </Text>
          </HintWrapper>

          {priceListUpdated && (
            <HintWrapper hint="Price has been updated">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                style={{
                  fontSize: '12px',
                  color: '#E67E22',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
                title="Price auto-updated"
                onClick={(e) => {
                  e.stopPropagation()
                  launchPriceAutoUpdatedModal?.()
                }}
              />
            </HintWrapper>
          )}
        </Box>

        {!hideDates && !!requiredByDate && (
          <Text color="bodyFontLightColor" fontSize="xsmall">
            DATE
          </Text>
        )}
      </Box>

      {!hidden && (
        <Box>
          <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" marginLeft="auto">
            <HintWrapper
              // @ts-expect-error TS2322
              hint={quoteItemSettingsHiddenText({ hideDates, hidePrices })}
            >
              {(hidePrices || hideDates)
                && <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '12px', marginRight: '4px' }} />}
            </HintWrapper>

            {/*
             // @ts-expect-error TS2769 */}
            {formatMoney(price_override)}
          </Text>
        </Box>
      )}
    </ListItem>
  )
})

export default CampaignQuoteTemplateItem
